import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { applyFormat } from '../../utils/caseTextMarks';

/**
 * 사례 글 편집 칸 위의 서식 단추 줄(2026-10-24) — 고른 글을 감싼다. 진하게·이탤릭·취소선·제목·목록·코드는 마크다운,
 * 색·크기는 정해진 표시(`{{red:…}}` 등, utils/caseTextMarks). 결과는 「미리보기」 탭과 상세 보기에서 그대로 보인다.
 * 단추를 눌러도 편집 칸의 선택이 풀리지 않게 mousedown 을 막는다.
 */
const TOOLS = [
    { id: 'bold', kind: 'wrap', before: '**', after: '**', label: 'B', cls: 'tfb-b' },
    { id: 'italic', kind: 'wrap', before: '*', after: '*', label: 'I', cls: 'tfb-i' },
    { id: 'strike', kind: 'wrap', before: '~~', after: '~~', label: 'S', cls: 'tfb-s' },
    { id: 'code', kind: 'wrap', before: '`', after: '`', label: '</>', cls: 'tfb-code' },
    { sep: true, id: 's1' },
    { id: 'heading', kind: 'line', before: '### ', label: 'H' },
    { id: 'list', kind: 'line', before: '- ', label: '•' },
    { sep: true, id: 's2' },
    { id: 'red', kind: 'mark', key: 'red', swatch: true },
    { id: 'blue', kind: 'mark', key: 'blue', swatch: true },
    { id: 'green', kind: 'mark', key: 'green', swatch: true },
    { id: 'mark', kind: 'mark', key: 'mark', swatch: true },
    { sep: true, id: 's3' },
    { id: 'big', kind: 'mark', key: 'big', label: 'A+' },
    { id: 'small', kind: 'mark', key: 'small', label: 'A−' },
];

export default function TextFormatBar({ targetRef, value, onChange }) {
    const { t } = useTranslation();
    const run = (tool) => {
        const el = targetRef.current;
        if (!el) return;
        const r = applyFormat(value, el.selectionStart, el.selectionEnd, { ...tool, placeholder: t('assistant:textFormat.placeholder') });
        onChange(r.value);
        // 바꾼 뒤 그 글을 다시 고른다 — 이어서 다른 서식을 겹칠 수 있게
        requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(r.start, r.end); } catch (_e) { /* 칸이 사라졌다 */ } });
    };
    return (
        <div className="tfb" role="toolbar" aria-label={t('assistant:textFormat.bar')}>
            {TOOLS.map(tool => (tool.sep
                ? <span key={tool.id} className="tfb-sep" aria-hidden="true" />
                : (
                    <button key={tool.id} type="button" className={`tfb-btn ${tool.cls || ''}`}
                        title={t(`assistant:textFormat.${tool.id}`)} aria-label={t(`assistant:textFormat.${tool.id}`)}
                        onMouseDown={(e) => e.preventDefault()} onClick={() => run(tool)}>
                        {tool.swatch ? <i className={`tfb-swatch rv-tx-${tool.key}`} /> : tool.label}
                    </button>
                )))}
        </div>
    );
}

TextFormatBar.propTypes = {
    targetRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};
